import isEmpty from 'lodash/isEmpty';
import pickBy from 'lodash/pickBy';

import { currentUserShowSuccess } from '../../ducks/user.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchConversationReadStatusApi, markConversationReadApi } from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { isTooManyRequestsError, storableError } from '../../util/errors';
import * as log from '../../util/log';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { fetchRecentMessages } from '../MessagesPage/MessagesPage.duck';
const { UUID } = sdkTypes;
const MESSAGES_PAGE_SIZE = 100;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ConversationPage/SET_INITIAL_VALUES';
export const FETCH_TRANSACTION_REQUEST = 'app/ConversationPage/FETCH_TRANSACTION_REQUEST';
export const FETCH_TRANSACTION_SUCCESS = 'app/ConversationPage/FETCH_TRANSACTION_SUCCESS';
export const FETCH_TRANSACTION_ERROR = 'app/ConversationPage/FETCH_TRANSACTION_ERROR';
export const FETCH_MESSAGES_REQUEST = 'app/ConversationPage/FETCH_MESSAGES_REQUEST';
export const FETCH_MESSAGES_SUCCESS = 'app/ConversationPage/FETCH_MESSAGES_SUCCESS';
export const FETCH_MESSAGES_ERROR = 'app/ConversationPage/FETCH_MESSAGES_ERROR';
export const FETCH_MESSAGES_SETTLE = 'app/ConversationPage/FETCH_MESSAGES_SETTLE';
export const SEND_MESSAGE_REQUEST = 'app/ConversationPage/SEND_MESSAGE_REQUEST';
export const SEND_MESSAGE_SUCCESS = 'app/ConversationPage/SEND_MESSAGE_SUCCESS';
export const SEND_MESSAGE_ERROR = 'app/ConversationPage/SEND_MESSAGE_ERROR';
export const MARK_READ_ERROR = 'app/ConversationPage/MARK_READ_ERROR';
export const SET_PEER_MESSAGE_READ_AT = 'app/ConversationPage/SET_PEER_MESSAGE_READ_AT';

// ================ Reducer ================ //

const initialState = {
  fetchTransactionInProgress: false,
  fetchTransactionError: null,
  transactionRef: null,
  fetchMessagesInProgress: false,
  fetchMessagesError: null,
  totalMessages: 0,
  totalMessagePages: 0,
  oldestMessagePageFetched: 0,
  messages: [],
  sendMessageInProgress: false,
  sendMessageError: null,
  markReadError: null,
  /** Integration API metadata.messageReadAt map (userId → ISO), for sender-side ticks. */
  peerMessageReadAt: {},
};

const mergeEntityArrays = (a, b) => {
  return a.filter(aEntity => !b.find(bEntity => aEntity.id.uuid === bEntity.id.uuid)).concat(b);
};

export default function conversationPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case FETCH_TRANSACTION_REQUEST:
      return { ...state, fetchTransactionInProgress: true, fetchTransactionError: null };
    case FETCH_TRANSACTION_SUCCESS: {
      const transactionRef = { id: payload.data.data.id, type: 'transaction' };
      return { ...state, fetchTransactionInProgress: false, transactionRef };
    }
    case FETCH_TRANSACTION_ERROR:
      console.error(payload);
      return { ...state, fetchTransactionInProgress: false, fetchTransactionError: payload };

    case FETCH_MESSAGES_REQUEST:
      return { ...state, fetchMessagesInProgress: true, fetchMessagesError: null };
    case FETCH_MESSAGES_SUCCESS: {
      const oldestMessagePageFetched =
        state.oldestMessagePageFetched > payload.page
          ? state.oldestMessagePageFetched
          : payload.page;
      return {
        ...state,
        fetchMessagesInProgress: false,
        messages: mergeEntityArrays(state.messages, payload.messages),
        totalMessages: payload.totalItems,
        totalMessagePages: payload.totalPages,
        oldestMessagePageFetched,
      };
    }
    case FETCH_MESSAGES_ERROR:
      return { ...state, fetchMessagesInProgress: false, fetchMessagesError: payload };
    case FETCH_MESSAGES_SETTLE:
      return { ...state, fetchMessagesInProgress: false };

    case SEND_MESSAGE_REQUEST:
      return { ...state, sendMessageInProgress: true, sendMessageError: null };
    case SEND_MESSAGE_SUCCESS:
      return { ...state, sendMessageInProgress: false };
    case SEND_MESSAGE_ERROR:
      return { ...state, sendMessageInProgress: false, sendMessageError: payload };
    case MARK_READ_ERROR:
      return { ...state, markReadError: payload };
    case SET_PEER_MESSAGE_READ_AT:
      return {
        ...state,
        peerMessageReadAt: { ...state.peerMessageReadAt, ...(payload || {}) },
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: initialValues,
});

const fetchTransactionRequest = () => ({ type: FETCH_TRANSACTION_REQUEST });
const fetchTransactionSuccess = response => ({
  type: FETCH_TRANSACTION_SUCCESS,
  payload: response,
});
const fetchTransactionError = e => ({ type: FETCH_TRANSACTION_ERROR, error: true, payload: e });

const fetchMessagesRequest = () => ({ type: FETCH_MESSAGES_REQUEST });
const fetchMessagesSuccess = (messages, pagination) => ({
  type: FETCH_MESSAGES_SUCCESS,
  payload: { messages, ...pagination },
});
const fetchMessagesError = e => ({ type: FETCH_MESSAGES_ERROR, error: true, payload: e });
const fetchMessagesSettle = () => ({ type: FETCH_MESSAGES_SETTLE });

const sendMessageRequest = () => ({ type: SEND_MESSAGE_REQUEST });
const sendMessageSuccess = () => ({ type: SEND_MESSAGE_SUCCESS });
const sendMessageError = e => ({ type: SEND_MESSAGE_ERROR, error: true, payload: e });
const markReadError = e => ({ type: MARK_READ_ERROR, error: true, payload: e });
const setPeerMessageReadAt = messageReadAt => ({
  type: SET_PEER_MESSAGE_READ_AT,
  payload: messageReadAt || {},
});

// ================ Thunks ================ //

const imageVariants = config => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(variantPrefix, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };
};

const fetchMessages = (txId, page, config, { soft = false } = {}) => (dispatch, getState, sdk) => {
  dispatch(fetchMessagesRequest());

  return sdk.messages
    .query({
      transaction_id: txId,
      include: ['sender', 'sender.profileImage'],
      ...imageVariants(config),
      page,
      perPage: MESSAGES_PAGE_SIZE,
    })
    .then(response => {
      const messages = denormalisedResponseEntities(response);
      const { totalItems, totalPages, page: fetchedPage } = response.data.meta;
      const pagination = { totalItems, totalPages, page: fetchedPage };
      const totalMessages = getState().ConversationPage.totalMessages;

      dispatch(fetchMessagesSuccess(messages, pagination));

      if (totalItems > totalMessages && page > 1) {
        dispatch(fetchMessages(txId, 1, config, { soft: true }));
      }
      return messages;
    })
    .catch(e => {
      const stored = storableError(e);
      if (soft || isTooManyRequestsError(e) || isTooManyRequestsError(stored)) {
        dispatch(fetchMessagesSettle());
        return null;
      }
      dispatch(fetchMessagesError(stored));
      // Do not rethrow — keeps polling / route loadData from surfacing CRA overlays.
      return null;
    });
};

export const fetchMoreMessages = (txId, config) => (dispatch, getState) => {
  const state = getState().ConversationPage;
  const { oldestMessagePageFetched, totalMessagePages } = state;
  const hasMoreOldMessages = totalMessagePages > oldestMessagePageFetched;
  const nextPage = hasMoreOldMessages ? oldestMessagePageFetched + 1 : oldestMessagePageFetched;

  return dispatch(fetchMessages(txId, nextPage, config));
};

export const fetchTransaction = (id, config) => (dispatch, getState, sdk) => {
  dispatch(fetchTransactionRequest());

  return sdk.transactions
    .show(
      {
        id,
        include: [
          'customer',
          'customer.profileImage',
          'provider',
          'provider.profileImage',
          'listing',
          'listing.images',
        ],
        ...imageVariants(config),
      },
      { expand: true }
    )
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchTransactionSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(fetchTransactionError(storableError(e)));
      // Soft-fail for route load — error lives in state; avoid uncaught rejection overlay.
      return null;
    });
};

/** Soft-fetch peer read receipts (Integration metadata via local API). */
export const fetchPeerMessageReadAt = txId => dispatch => {
  const txIdStr = txId?.uuid || txId;
  if (!txIdStr || typeof window === 'undefined') {
    return Promise.resolve({});
  }

  return fetchConversationReadStatusApi(txIdStr)
    .then(response => {
      const messageReadAt = response?.messageReadAt || {};
      dispatch(setPeerMessageReadAt(messageReadAt));
      return messageReadAt;
    })
    .catch(() => {
      // Optional: Integration credentials / network — keep last known map.
      return {};
    });
};

export const markConversationRead = txId => (dispatch, getState, sdk) => {
  const { isAuthenticated } = getState().auth;
  const txIdStr = txId?.uuid || txId;

  if (!isAuthenticated || !txIdStr) {
    return Promise.resolve({});
  }

  const privateData = getState()?.user?.currentUser?.attributes?.profile?.privateData || {};
  const messageReadAt = privateData.messageReadAt || {};
  const queryParams = {
    expand: true,
    include: ['profileImage'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };
  const payload = {
    privateData: {
      messageReadAt: {
        ...messageReadAt,
        [txIdStr]: new Date().toISOString(),
      },
    },
  };

  // Cross-party ticks: Integration metadata (Marketplace privateData is not visible to peers).
  const peerReceiptPromise = markConversationReadApi(txIdStr).catch(e => {
    log.error(e, 'mark-conversation-read-api-failed', { txId: txIdStr });
    return null;
  });

  return Promise.all([
    sdk.currentUser
      .updateProfile(payload, queryParams)
      .then(response => {
        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        // Unread badge/dots clear via updated privateData.messageReadAt on currentUser.
        dispatch(currentUserShowSuccess(entities[0]));
        return entities[0];
      })
      .catch(e => {
        dispatch(markReadError(storableError(e)));
        log.error(e, 'mark-conversation-read-failed', { txId: txIdStr });
      }),
    peerReceiptPromise,
  ]).then(([user]) => user);
};

export const sendMessage = (txId, message, config) => (dispatch, getState, sdk) => {
  dispatch(sendMessageRequest());

  return sdk.messages
    .send({ transactionId: txId, content: message })
    .then(response => {
      const messageId = response.data.data.id;
      return dispatch(fetchMessages(txId, 1, config))
        .then(() => dispatch(markConversationRead(txId)))
        .then(() => {
          // Refresh shared conversations store so topbar unread badge stays in sync.
          dispatch(fetchRecentMessages({ ignoreRateLimit: true }));
          dispatch(sendMessageSuccess());
          return messageId;
        })
        .catch(() => {
          dispatch(fetchRecentMessages({ ignoreRateLimit: true }));
          dispatch(sendMessageSuccess());
          return messageId;
        });
    })
    .catch(e => {
      dispatch(sendMessageError(storableError(e)));
      throw e;
    });
};

const isNonEmpty = value => {
  return typeof value === 'object' || Array.isArray(value) ? !isEmpty(value) : !!value;
};

/** Refresh newest messages + peer read receipts for polling / focus. Soft-fails. */
export const refreshConversationMessages = (txId, config) => dispatch =>
  Promise.all([
    dispatch(fetchMessages(txId, 1, config, { soft: true })),
    dispatch(fetchPeerMessageReadAt(txId)),
  ]);

export const loadData = (params, search, config) => (dispatch, getState) => {
  const txId = new UUID(params.id);
  const state = getState().ConversationPage;
  const initialValues = state.transactionRef ? {} : pickBy(state, isNonEmpty);
  dispatch(setInitialValues(initialValues));

  return Promise.all([
    dispatch(fetchTransaction(txId, config)),
    dispatch(fetchMessages(txId, 1, config)),
  ]).then(() =>
    Promise.all([dispatch(markConversationRead(txId)), dispatch(fetchPeerMessageReadAt(txId))])
  );
};
